import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import { Slider } from "@/components/ui/slider";
import { deviceService } from "@/services/deviceService";
import { locationService, Country, City, District } from "@/services/locationService";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Building2, MapPin, CheckIcon, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";

interface AddDeviceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companies: any[];
  onSuccess: () => void;
}

export default function AddDeviceDialog({
  open,
  onOpenChange,
  companies,
  onSuccess,
}: AddDeviceDialogProps) {
  const [loading, setLoading] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [selectedCity, setSelectedCity] = useState<string | null>(null);
  const [countrySearchTerm, setCountrySearchTerm] = useState("");
  const [citySearchTerm, setCitySearchTerm] = useState("");
  const [districtSearchTerm, setDistrictSearchTerm] = useState("");
  const [isCountryOpen, setIsCountryOpen] = useState(false);
  const [isCityOpen, setIsCityOpen] = useState(false);
  const [isDistrictOpen, setIsDistrictOpen] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    token: "",
    company: "",
    volume: 100,
    location: {
      country: {
        code: "",
        name: ""
      },
      city: {
        stateCode: "",
        name: ""
      },
      district: {
        name: "",
        coordinates: {
          latitude: 0,
          longitude: 0
        }
      },
      address: ""
    }
  });

  const [selectedCityName, setSelectedCityName] = useState("");
  const [selectedDistrictName, setSelectedDistrictName] = useState("");

  // Fetch countries
  const { data: countries = [], isLoading: isLoadingCountries } = useQuery<Country[]>({
    queryKey: ['countries'],
    queryFn: () => locationService.getCountries(),
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  // Fetch cities based on selected country
  const { data: cities = [], isLoading: isLoadingCities } = useQuery<City[]>({
    queryKey: ['cities', selectedCountry],
    queryFn: () => locationService.getCities(selectedCountry!),
    enabled: !!selectedCountry,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  // Fetch districts based on selected country and city
  const { data: districts = [], isLoading: isLoadingDistricts } = useQuery<District[]>({
    queryKey: ['districts', selectedCountry, selectedCity],
    queryFn: () => locationService.getDistricts(selectedCountry!, selectedCity!),
    enabled: !!selectedCountry && !!selectedCity,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  // Reset city and district when country changes
  useEffect(() => {
    if (selectedCountry) {
      setFormData(prev => ({
        ...prev,
        location: {
          ...prev.location,
          city: {
            stateCode: '',
            name: ''
          },
          district: {
            name: '',
            coordinates: {
              latitude: 0,
              longitude: 0
            }
          }
        }
      }));
      setSelectedCity(null);
      setSelectedCityName("");
      setSelectedDistrictName("");
      setCitySearchTerm("");
      setDistrictSearchTerm("");
    }
  }, [selectedCountry]);

  // Reset district when city changes
  useEffect(() => {
    if (selectedCity) {
      setFormData(prev => ({
        ...prev,
        location: {
          ...prev.location,
          district: {
            name: '',
            coordinates: {
              latitude: 0,
              longitude: 0
            }
          }
        }
      }));
      setSelectedDistrictName("");
      setDistrictSearchTerm("");
    }
  }, [selectedCity]);

  // Turkish character normalization for search
  const simplifyTurkishText = (text: string) => {
    return text.toLowerCase()
      .replace(/ı/g, "i")
      .replace(/i̇/g, "i")
      .replace(/İ/g, "i")
      .replace(/ş/g, "s")
      .replace(/Ş/g, "s")
      .replace(/ğ/g, "g")
      .replace(/Ğ/g, "g")
      .replace(/ü/g, "u")
      .replace(/Ü/g, "u")
      .replace(/ö/g, "o")
      .replace(/Ö/g, "o")
      .replace(/ç/g, "c")
      .replace(/Ç/g, "c");
  };

  // Filtered lists with memoization
  const filteredCountries = useMemo<Country[]>(() => {
    if (!countrySearchTerm) return (countries as Country[]).slice(0, 100);
    const normalizedSearch = simplifyTurkishText(countrySearchTerm);
    return (countries as Country[]).filter(country =>
      simplifyTurkishText(country.name).includes(normalizedSearch)
    ).slice(0, 100);
  }, [countries, countrySearchTerm]);

  const filteredCities = useMemo<City[]>(() => {
    if (!citySearchTerm) return (cities as City[]).slice(0, 100);
    const normalizedSearch = simplifyTurkishText(citySearchTerm);
    return (cities as City[]).filter(city =>
      simplifyTurkishText(city.name).includes(normalizedSearch)
    ).slice(0, 100);
  }, [cities, citySearchTerm]);

  const filteredDistricts = useMemo<District[]>(() => {
    if (!districtSearchTerm) return (districts as District[]).slice(0, 100);
    const normalizedSearch = simplifyTurkishText(districtSearchTerm);
    return (districts as District[]).filter(district =>
      simplifyTurkishText(district.name).includes(normalizedSearch)
    ).slice(0, 100);
  }, [districts, districtSearchTerm]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.token || !formData.company) {
      toast.error("Lütfen tüm zorunlu alanları doldurun");
      return;
    }

    try {
      setLoading(true);
      await deviceService.createDevice({
        name: formData.name,
        token: formData.token,
        company: formData.company,
        location: {
          country: formData.location.country,
          city: formData.location.city.name,
          district: formData.location.district.name,
          address: formData.location.address
        },
      });

      toast.success("Cihaz başarıyla oluşturuldu");
      onSuccess();
      onOpenChange(false);

      // Reset form
      setFormData({
        name: "",
        token: "",
        company: "",
        volume: 100,
        location: {
          country: { code: "", name: "" },
          city: {
            stateCode: "",
            name: ""
          },
          district: {
            name: "",
            coordinates: {
              latitude: 0,
              longitude: 0
            }
          },
          address: ""
        }
      });
      setSelectedCountry(null);
      setSelectedCity(null);
      setSelectedCityName("");
      setSelectedDistrictName("");
      setCountrySearchTerm("");
      setCitySearchTerm("");
      setDistrictSearchTerm("");
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Cihaz oluşturulurken hata oluştu");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={true}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Yeni Cihaz Ekle</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Device Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Cihaz Adı</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              placeholder="Örn: Mağaza 1 - Ana Salon"
              required
            />
          </div>

          {/* Token */}
          <div className="space-y-2">
            <Label htmlFor="token">Token</Label>
            <Input
              id="token"
              value={formData.token}
              onChange={(e) =>
                setFormData({ ...formData, token: e.target.value.toUpperCase() })
              }
              placeholder="6 haneli kod"
              maxLength={6}
              required
            />
          </div>

          {/* Company */}
          <div className="space-y-2">
            <Label htmlFor="company">Firma</Label>
            <Select
              value={formData.company}
              onValueChange={(value) =>
                setFormData({ ...formData, company: value })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Firma seçin" />
              </SelectTrigger>
              <SelectContent>
                {companies.map((company) => (
                  <SelectItem key={company._id} value={company._id}>
                    {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Country Selection */}
          <div className="space-y-2">
            <Label>Ülke</Label>
            <Popover open={isCountryOpen} onOpenChange={setIsCountryOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  role="combobox"
                  aria-expanded={isCountryOpen}
                  className={cn(
                    "w-full justify-between",
                    !formData.location.country.name && "text-muted-foreground"
                  )}
                >
                  <div className="flex items-center gap-2">
                    {formData.location.country.name && (
                      <div className="relative w-6 h-4 rounded-sm overflow-hidden flex items-center justify-center border border-gray-200">
                        <img
                          src={`https://flagcdn.com/${formData.location.country.code?.toLowerCase() || 'xx'}.svg`}
                          alt={`${formData.location.country.name} flag`}
                          className="h-full w-full object-cover"
                          loading="lazy"
                        />
                      </div>
                    )}
                    <span className="truncate">
                      {formData.location.country.name || "Ülke seçin"}
                    </span>
                  </div>
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="z-[100] w-[var(--radix-popover-trigger-width)] p-0" align="start" sideOffset={8}>
                <Command className="overflow-hidden rounded-lg border shadow-md">
                  <CommandInput
                    placeholder="Ara..."
                    className="h-9"
                    value={countrySearchTerm}
                    onValueChange={setCountrySearchTerm}
                  />
                  {isLoadingCountries ? (
                    <div className="py-6 text-center text-sm text-muted-foreground">
                      Yükleniyor...
                    </div>
                  ) : (
                    <CommandEmpty className="py-2 text-center text-sm">
                      Ülke bulunamadı
                    </CommandEmpty>
                  )}
                  <CommandGroup className="max-h-56 overflow-auto">
                    {filteredCountries.map((country) => (
                      <CommandItem
                        key={country.code}
                        value={country.name}
                        onSelect={(currentValue) => {
                          const selected = filteredCountries.find(c => c.name === currentValue);
                          if (selected) {
                            setFormData(prev => ({
                              ...prev,
                              location: {
                                country: { code: selected.code, name: selected.name },
                                city: { stateCode: "", name: "" },
                                district: { name: "", coordinates: { latitude: 0, longitude: 0 } },
                                address: ""
                              }
                            }));
                            setSelectedCountry(selected.code);
                            setSelectedCity(null);
                            setSelectedCityName("");
                            setSelectedDistrictName("");
                          }
                          setIsCountryOpen(false);
                        }}
                        className="flex items-center gap-2 py-1.5"
                      >
                        <div className="relative w-6 h-4 rounded-sm overflow-hidden flex items-center justify-center border border-gray-200">
                          <img
                            src={`https://flagcdn.com/${country.code.toLowerCase()}.svg`}
                            alt={`${country.name} flag`}
                            className="h-full w-full object-cover"
                            loading="lazy"
                          />
                        </div>
                        <span className="truncate">{country.name}</span>
                        {country.code === formData.location.country.code && (
                          <CheckIcon className="ml-auto h-4 w-4 flex-shrink-0 text-primary" />
                        )}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* City Selection */}
          <div className="space-y-2">
            <Label>Şehir</Label>
            <Popover open={isCityOpen} onOpenChange={setIsCityOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  role="combobox"
                  aria-expanded={isCityOpen}
                  disabled={loading || !selectedCountry}
                  className={cn(
                    "w-full justify-between",
                    !selectedCityName && "text-muted-foreground",
                    !selectedCountry && "opacity-70 bg-muted"
                  )}
                >
                  <div className="flex items-center gap-2">
                    {selectedCityName && <Building2 className="h-4 w-4 text-green-500" />}
                    <span className="truncate">
                      {selectedCityName || (selectedCountry ? "Şehir seçin" : "Önce ülke seçin")}
                    </span>
                  </div>
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="z-[100] w-[var(--radix-popover-trigger-width)] p-0" align="start" sideOffset={8}>
                <Command className="overflow-hidden rounded-lg border shadow-md">
                  <CommandInput
                    placeholder="Ara..."
                    className="h-9"
                    value={citySearchTerm}
                    onValueChange={setCitySearchTerm}
                  />
                  {isLoadingCities ? (
                    <div className="py-6 text-center text-sm text-muted-foreground">
                      Yükleniyor...
                    </div>
                  ) : (
                    <CommandEmpty className="py-2 text-center text-sm">
                      Şehir bulunamadı
                    </CommandEmpty>
                  )}
                  <CommandGroup className="max-h-56 overflow-auto">
                    {filteredCities.map((city) => (
                      <CommandItem
                        key={city.stateCode}
                        value={city.name}
                        onSelect={() => {
                          setFormData(prev => ({
                            ...prev,
                            location: {
                              ...prev.location,
                              city: {
                                stateCode: city.stateCode,
                                name: city.name
                              },
                              district: {
                                name: "",
                                coordinates: { latitude: 0, longitude: 0 }
                              }
                            }
                          }));
                          setSelectedCity(city.stateCode);
                          setSelectedCityName(city.name);
                          setSelectedDistrictName("");
                          setIsCityOpen(false);
                        }}
                        className="flex items-center gap-2 py-1.5"
                      >
                        <Building2 className="h-4 w-4 text-green-500" />
                        <span className="truncate">{city.name}</span>
                        {city.stateCode === selectedCity && (
                          <CheckIcon className="ml-auto h-4 w-4 flex-shrink-0 text-primary" />
                        )}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* District Selection */}
          <div className="space-y-2">
            <Label>İlçe</Label>
            <Popover open={isDistrictOpen} onOpenChange={setIsDistrictOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  role="combobox"
                  aria-expanded={isDistrictOpen}
                  disabled={loading || !selectedCity}
                  className={cn(
                    "w-full justify-between",
                    !selectedDistrictName && "text-muted-foreground",
                    !selectedCity && "opacity-70 bg-muted"
                  )}
                >
                  <div className="flex items-center gap-2">
                    {selectedDistrictName && <MapPin className="h-4 w-4 text-red-500" />}
                    <span className="truncate">
                      {selectedDistrictName || (selectedCity ? "İlçe seçin" : "Önce şehir seçin")}
                    </span>
                  </div>
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="z-[100] w-[var(--radix-popover-trigger-width)] p-0" align="start" sideOffset={8}>
                <Command className="overflow-hidden rounded-lg border shadow-md">
                  <CommandInput
                    placeholder="Ara..."
                    className="h-9"
                    value={districtSearchTerm}
                    onValueChange={setDistrictSearchTerm}
                  />
                  {isLoadingDistricts ? (
                    <div className="py-6 text-center text-sm text-muted-foreground">
                      Yükleniyor...
                    </div>
                  ) : (
                    <CommandEmpty className="py-2 text-center text-sm">
                      İlçe bulunamadı
                    </CommandEmpty>
                  )}
                  <CommandGroup className="max-h-56 overflow-auto">
                    {filteredDistricts.map((district) => (
                      <CommandItem
                        key={district.name}
                        value={district.name}
                        onSelect={() => {
                          setFormData(prev => ({
                            ...prev,
                            location: {
                              ...prev.location,
                              district: {
                                name: district.name,
                                coordinates: {
                                  latitude: district.coordinates.latitude,
                                  longitude: district.coordinates.longitude
                                }
                              }
                            }
                          }));
                          setSelectedDistrictName(district.name);
                          setIsDistrictOpen(false);
                        }}
                        className="flex items-center gap-2 py-1.5"
                      >
                        <MapPin className="h-4 w-4 text-red-500" />
                        <span className="truncate">{district.name}</span>
                        {district.name === selectedDistrictName && (
                          <CheckIcon className="ml-auto h-4 w-4 flex-shrink-0 text-primary" />
                        )}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* Volume Slider */}
          <div className="space-y-2">
            <Label htmlFor="volume">Varsayılan Ses Seviyesi</Label>
            <div className="flex items-center gap-4">
              <Slider
                value={[formData.volume]}
                onValueChange={(values) =>
                  setFormData({ ...formData, volume: values[0] })
                }
                min={0}
                max={100}
                step={1}
                className="flex-1"
              />
              <div className="w-12 text-right">
                <span className="text-sm font-medium">%{formData.volume}</span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              İptal
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Oluşturuluyor..." : "Cihaz Ekle"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
